import * as amplitude from '@amplitude/analytics-browser'
import { plugin as engagementPlugin } from '@amplitude/engagement-browser'
import { getAmplitudeApiKey } from 'amplitude/amplitudeConfig'

let initialized = false

/**
 * Track a named event (for studio and other non–event-map callers).
 * Exposed on `window.trackOpenSolarAmplitudeEvent` after init.
 */
export const trackAmplitudeEventByName = (
  eventName: string,
  eventProperties?: Record<string, unknown>
): boolean => {
  if (!initAmplitudeBrowser()) {
    return false
  }
  try {
    amplitude.track(eventName, eventProperties ?? {})
    return true
  } catch (ex) {
    console.warn('[Amplitude] track failed', ex)
    return false
  }
}

/**
 * Initializes Amplitude Browser SDK 2 with Guides & Surveys (engagement plugin).
 * Safe to call multiple times; only the first call with a valid API key runs init.
 */
export const initAmplitudeBrowser = (): boolean => {
  if (initialized) {
    return true
  }

  const apiKey = getAmplitudeApiKey()
  if (!apiKey) {
    if (window.debugAmplitude) {
      console.log('[Amplitude] Skipping init — API key is not configured')
    }
    return false
  }

  try {
    amplitude.add(
      engagementPlugin({
        options: {
          logLevel: window.debugAmplitude ? 4 : 2,
        },
      })
    )

    amplitude.init(apiKey, undefined, {
      autocapture: false,
      flushQueueSize: 30,
      flushIntervalMillis: 10,
      identityStorage: 'localStorage',
      // OpenSolar user IDs are numeric DB primary keys; many are <5 chars (Amplitude HTTP API default min).
      minIdLength: 1,
    })

    window.trackOpenSolarAmplitudeEvent = trackAmplitudeEventByName
    initialized = true

    if (window.debugAmplitude) {
      console.log('[Amplitude] Browser SDK 2 + Guides & Surveys initialized')
    }

    return true
  } catch (ex) {
    console.warn('[Amplitude] Init failed', ex)
    return false
  }
}

export const isAmplitudeBrowserInitialized = (): boolean => initialized

/** @internal Test-only reset for module state between vitest cases. */
export const resetAmplitudeBrowserStateForTests = (): void => {
  initialized = false
  delete window.trackOpenSolarAmplitudeEvent
}

export { amplitude }
